import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { listInvoices, getInvoicePdf } from '../services/api';
import CreateInvoiceModal from '../components/invoicing/CreateInvoiceModal';
import InvoiceDetailModal from '../components/invoicing/InvoiceDetailModal';
import CreateCreditNoteModal from '../components/invoicing/CreateCreditNoteModal';
import HelpIcon from '../components/help/HelpIcon';
import { getDateLocale } from '../utils/dateLocale';
import { storage } from '../utils/storage';
import './InvoicingPage.css';

const DOCUMENT_TYPES = {
  invoice: { label: 'Factura', icon: 'receipt_long', color: 'var(--color-primary)' },
  credit_note: { label: 'Nota Crédito', icon: 'remove_circle', color: 'var(--color-danger)' },
  debit_note: { label: 'Nota Débito', icon: 'add_circle', color: 'var(--color-warning)' },
};

const InvoicingPage = () => {
  const { t, i18n } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [filterType, setFilterType] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [creditNoteInvoice, setCreditNoteInvoice] = useState(null);
  const [actionError, setActionError] = useState(null);

  const restaurantId = storage.get('selectedRestaurantId');

  const selectedYear = selectedMonth.split('-')[0];
  const selectedMonthNum = selectedMonth.split('-')[1];

  const monthNames = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language, { month: 'long' });
    return Array.from({ length: 12 }, (_, i) => {
      const name = formatter.format(new Date(2000, i, 1));
      return name.charAt(0).toUpperCase() + name.slice(1);
    });
  }, [i18n.language]);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = { month: selectedMonth };
      if (filterType) params.type = filterType;
      const result = await listInvoices(restaurantId, params);
      setInvoices(result.data || []);
      setTotals(result.totals || {});
    } catch (err) {
      console.error('Error loading invoices:', err);
      setActionError(t('invoicing.loadError', 'Error al cargar las facturas'));
    } finally {
      setLoading(false);
    }
  }, [restaurantId, selectedMonth, filterType, t]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const result = await getInvoicePdf(invoiceId, restaurantId);
      window.open(result.data.pdfUrl, '_blank');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setActionError(t('invoicing.pdfError', 'Error al descargar el PDF'));
    }
  };

  const handleCreateCreditNote = (invoice) => {
    setCreditNoteInvoice(invoice);
    setShowCreditNoteModal(true);
  };

  const formatNumber = (num) =>
    `$${(num || 0).toLocaleString(getDateLocale(i18n.language), { minimumFractionDigits: 2 })}`;

  const formatComprobante = (inv) => {
    const pv = String(inv.puntoDeVenta || 0).padStart(5, '0');
    const num = String(inv.numeroComprobante || 0).padStart(8, '0');
    return `${inv.invoiceClass || ''} ${pv}-${num}`;
  };

  return (
    <div className="invoicing-page">
      {actionError && (
        <div className="invoicing-action-error" role="alert" aria-live="assertive">
          <span className="material-icons">error</span>
          {actionError}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setActionError(null)}
            aria-label={t('common.close', 'Cerrar')}
          >
            <span className="material-icons">close</span>
          </button>
        </div>
      )}
      <div className="invoicing-header">
        <div>
          <h1>
            {t('invoicing.title', 'Facturación')} <HelpIcon topicKey="invoicing.overview" />
          </h1>
          <p className="invoicing-subtitle">
            {t('invoicing.subtitle', 'Facturas electrónicas, notas de crédito y débito')}
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <span className="material-icons">add</span>
          {t('invoicing.newInvoice', 'Nueva Factura')}
        </button>
      </div>

      {/* Totals */}
      <div className="invoicing-totals">
        <div className="invoicing-total-card">
          <span className="material-icons">receipt_long</span>
          <div>
            <span className="total-label">{t('invoicing.invoiced', 'Facturado')}</span>
            <span className="total-amount">{formatNumber(totals.totalFacturado)}</span>
            <span className="total-count">
              {totals.countFacturas || 0} {t('invoicing.invoices', 'facturas')}
            </span>
          </div>
        </div>
        <div className="invoicing-total-card credit">
          <span className="material-icons">remove_circle</span>
          <div>
            <span className="total-label">{t('invoicing.creditNotes', 'Notas de Crédito')}</span>
            <span className="total-amount">{formatNumber(totals.totalNC)}</span>
            <span className="total-count">{totals.countNC || 0} NC</span>
          </div>
        </div>
        <div className="invoicing-total-card debit">
          <span className="material-icons">add_circle</span>
          <div>
            <span className="total-label">{t('invoicing.debitNotes', 'Notas de Débito')}</span>
            <span className="total-amount">{formatNumber(totals.totalND)}</span>
            <span className="total-count">{totals.countND || 0} ND</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="invoicing-filters">
        <div className="invoicing-month-picker">
          <label htmlFor="invoicing-month-select" className="sr-only">
            {t('invoicing.filterMonth', 'Mes')}
          </label>
          <select
            id="invoicing-month-select"
            value={selectedMonthNum}
            onChange={(e) => setSelectedMonth(`${selectedYear}-${e.target.value}`)}
            className="invoicing-month-input"
          >
            {monthNames.map((name, i) => (
              <option key={i} value={String(i + 1).padStart(2, '0')}>
                {name}
              </option>
            ))}
          </select>
          <label htmlFor="invoicing-year-select" className="sr-only">
            {t('invoicing.filterYear', 'Año')}
          </label>
          <select
            id="invoicing-year-select"
            value={selectedYear}
            onChange={(e) => setSelectedMonth(`${e.target.value}-${selectedMonthNum}`)}
            className="invoicing-month-input"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <label htmlFor="invoicing-type-filter" className="sr-only">
          {t('invoicing.filterType', 'Tipo de comprobante')}
        </label>
        <select
          id="invoicing-type-filter"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="invoicing-type-select"
        >
          <option value="">{t('invoicing.allTypes', 'Todos los tipos')}</option>
          <option value="invoice">{t('invoicing.invoicesOnly', 'Facturas')}</option>
          <option value="credit_note">{t('invoicing.creditNotesOnly', 'Notas de Crédito')}</option>
          <option value="debit_note">{t('invoicing.debitNotesOnly', 'Notas de Débito')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="invoicing-table-wrapper">
        <table className="invoicing-table">
          <thead>
            <tr>
              <th>{t('invoicing.type', 'Tipo')}</th>
              <th>{t('invoicing.number', 'Número')}</th>
              <th>{t('invoicing.date', 'Fecha')}</th>
              <th>{t('invoicing.receptor', 'Receptor')}</th>
              <th>
                {t('invoicing.cae', 'CAE')} <HelpIcon topicKey="invoicing.cae" />
              </th>
              <th className="text-right">{t('invoicing.amount', 'Importe')}</th>
              <th>{t('invoicing.status', 'Estado')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center">
                  {t('common.loading', 'Cargando...')}
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center">
                  {t('invoicing.noInvoices', 'No hay comprobantes para este período')}
                </td>
              </tr>
            ) : (
              invoices.map((inv) => {
                const docType = DOCUMENT_TYPES[inv.documentType] || DOCUMENT_TYPES.invoice;
                return (
                  <tr key={inv.invoiceId} onClick={() => setSelectedInvoice(inv)}>
                    <td>
                      <span className="invoicing-doc-type" style={{ color: docType.color }}>
                        <span className="material-icons">{docType.icon}</span>
                        {docType.label} {inv.invoiceClass}
                      </span>
                    </td>
                    <td className="mono">{formatComprobante(inv)}</td>
                    <td>
                      {new Date(inv.createdAt).toLocaleDateString(getDateLocale(i18n.language))}
                    </td>
                    <td>{inv.receptorNombre || 'Consumidor Final'}</td>
                    <td className="mono">{inv.cae || '-'}</td>
                    <td className="text-right mono">{formatNumber(inv.importeTotal)}</td>
                    <td>
                      <span className={`invoicing-status ${inv.status}`}>
                        {inv.status === 'authorized'
                          ? t('invoicing.authorized', 'Autorizada')
                          : inv.status === 'rejected'
                            ? t('invoicing.rejected', 'Rechazada')
                            : t('invoicing.error', 'Error')}
                      </span>
                    </td>
                    <td>
                      <div className="invoicing-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleDownloadPdf(inv.invoiceId)}
                          title="PDF"
                        >
                          <span className="material-icons">picture_as_pdf</span>
                        </button>
                        {inv.documentType === 'invoice' && inv.status === 'authorized' && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleCreateCreditNote(inv)}
                            title="Nota de Crédito"
                          >
                            <span className="material-icons">undo</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateInvoiceModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadInvoices();
          }}
        />
      )}

      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onCreditNote={(inv) => {
            setSelectedInvoice(null);
            handleCreateCreditNote(inv);
          }}
          onDownloadPdf={handleDownloadPdf}
        />
      )}

      {showCreditNoteModal && creditNoteInvoice && (
        <CreateCreditNoteModal
          invoice={creditNoteInvoice}
          onClose={() => {
            setShowCreditNoteModal(false);
            setCreditNoteInvoice(null);
          }}
          onCreated={() => {
            setShowCreditNoteModal(false);
            setCreditNoteInvoice(null);
            loadInvoices();
          }}
        />
      )}
    </div>
  );
};

export default InvoicingPage;
